import React, { useState, useEffect, useMemo } from "react";
import { usersAPI, roomsAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import { User, UserRole } from "../types/auth";
import { Room } from "../types/room";
import Pagination from "../components/ui/Pagination";

const PAGE_SIZE = 10;

function generatePassword(len = 10): string {
  const chars = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789!@#$";
  return Array.from({ length: len }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
}

interface UserFormData {
  name: string;
  email: string;
  password: string;
  role: UserRole;
  managedRooms: string[];
}

function UsersPage(): React.ReactElement {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === "superadmin";
  const isAdmin = currentUser?.role === "admin";
  const isMasterAdmin = currentUser?.email === "admin@23homestay.vn";
  // Server trả về "id" (không phải "_id"), cần handle cả hai
  const currentUserId = currentUser?._id || (currentUser as any)?.id;

  const [users, setUsers] = useState<User[]>([]);
  const [search, setSearch] = useState<string>("");
  const [filterRole, setFilterRole] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [viewingUser, setViewingUser] = useState<User | null>(null);
  const [banTarget, setBanTarget] = useState<User | null>(null);
  const [banReason, setBanReason] = useState<string>("");
  const [showPassword, setShowPassword] = useState(false);
  const [savedPassword, setSavedPassword] = useState<string | null>(null);
  const [formData, setFormData] = useState<UserFormData>({
    name: "",
    email: "",
    password: "",
    role: "roommaster",
    managedRooms: [],
  });

  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      const matchSearch = !search ||
        u.name.toLowerCase().includes(search.toLowerCase()) ||
        u.email.toLowerCase().includes(search.toLowerCase());
      const matchRole = filterRole === "all" || u.role === filterRole;
      return matchSearch && matchRole;
    });
  }, [users, search, filterRole]);

  useEffect(() => { setPage(1); }, [search, filterRole]);

  const totalPages = Math.ceil(filteredUsers.length / PAGE_SIZE);
  const pageUsers = filteredUsers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async (): Promise<void> => {
    try {
      const [usersRes, roomsRes] = await Promise.all([
        usersAPI.getAll(),
        roomsAPI.getAll(),
      ]);
      setUsers(usersRes.data.data);
      setRooms(roomsRes.data.data);
      setLoading(false);
    } catch (error) {
      toast.error("Lỗi khi tải dữ liệu");
      setLoading(false);
    }
  };

  const fetchUsers = async (): Promise<void> => {
    try {
      const [usersRes, roomsRes] = await Promise.all([
        usersAPI.getAll(),
        roomsAPI.getAll(),
      ]);
      setUsers(usersRes.data.data);
      setRooms(roomsRes.data.data);
    } catch (error) {
      toast.error("Lỗi khi tải danh sách người dùng");
    }
  };

  // Kiểm tra quyền sửa user
  const canEdit = (targetUser: User): boolean => {
    if (isSuperAdmin) {
      // SuperAdmin: chỉ không sửa được SuperAdmin khác
      return !(targetUser.role === "superadmin" && targetUser._id !== currentUserId);
    }
    if (isAdmin) {
      // Admin: chỉ sửa user, roommaster, kiemkho, hoặc bản thân
      return targetUser.role === "user" || targetUser.role === "roommaster" || targetUser.role === "kiemkho" || targetUser._id === currentUserId;
    }
    return false;
  };

  const canAdd = isSuperAdmin || isAdmin;

  // Role options theo quyền người đang đăng nhập
  const getRoleOptions = (): UserRole[] => {
    if (isSuperAdmin) return ["user", "roommaster", "kiemkho", "admin", "superadmin"];
    return ["user", "roommaster", "kiemkho"];
  };

  // Role options khi đang edit (admin sửa bản thân thì không đổi được role)
  const getEditRoleOptions = (): UserRole[] => {
    if (editingUser && editingUser._id === currentUser?._id) {
      // Không cho đổi role của chính mình
      return [editingUser.role];
    }
    return getRoleOptions();
  };

  const handleAddNew = (): void => {
    setEditingUser(null);
    setFormData({
      name: "",
      email: "",
      password: "",
      role: "roommaster",
      managedRooms: [],
    });
    setShowModal(true);
  };

  const handleEdit = (user: User): void => {
    setEditingUser(user);
    let normalizedRooms: string[];
    if (Array.isArray(user.managedRooms) && user.managedRooms.length > 0) {
      normalizedRooms = user.managedRooms.map((r) => (typeof r === "object" && r._id ? r._id : r as string));
    } else {
      normalizedRooms = [];
    }
    setFormData({
      name: user.name,
      email: user.email,
      password: "",
      role: user.role || "roommaster",
      managedRooms: normalizedRooms,
    });
    setShowModal(true);
  };

  const handleBan = async (): Promise<void> => {
    if (!banTarget) return;
    try {
      await usersAPI.ban(banTarget._id, banReason);
      toast.success(`Đã khóa tài khoản ${banTarget.name}`);
      setBanTarget(null);
      setBanReason("");
      fetchUsers();
    } catch {
      toast.error("Lỗi khi khóa tài khoản");
    }
  };

  const handleUnban = async (user: User): Promise<void> => {
    try {
      await usersAPI.unban(user._id);
      toast.success(`Đã mở khóa tài khoản ${user.name}`);
      fetchUsers();
    } catch {
      toast.error("Lỗi khi mở khóa tài khoản");
    }
  };

  const handleApproveCmnd = async (user: User): Promise<void> => {
    try {
      await usersAPI.approveCmnd(user._id);
      toast.success(`Đã duyệt xác thực cấp 2 cho ${user.name}`);
      fetchUsers();
    } catch {
      toast.error("Lỗi khi duyệt xác thực");
    }
  };

  const handleRejectCmnd = async (user: User): Promise<void> => {
    const reason = window.prompt("Lý do từ chối (tuỳ chọn):", "");
    if (reason === null) return; // admin bấm Hủy
    try {
      await usersAPI.rejectCmnd(user._id, reason);
      toast.success(`Đã từ chối ảnh CCCD của ${user.name}`);
      fetchUsers();
    } catch {
      toast.error("Lỗi khi từ chối");
    }
  };

  const handleDelete = async (id: string): Promise<void> => {
    if (currentUserId === id) {
      toast.error("Không thể xóa chính mình!");
      return;
    }

    if (window.confirm("Bạn có chắc muốn xóa người dùng này?")) {
      try {
        await usersAPI.delete(id);
        toast.success("Đã xóa người dùng thành công");
        fetchUsers();
      } catch (error) {
        toast.error("Lỗi khi xóa người dùng");
      }
    }
  };

  const toggleRoomSelection = (roomId: string): void => {
    if (!Array.isArray(formData.managedRooms)) {
      setFormData({ ...formData, managedRooms: [roomId] });
    } else {
      const isSelected = formData.managedRooms.includes(roomId);
      if (isSelected) {
        setFormData({
          ...formData,
          managedRooms: formData.managedRooms.filter((id) => id !== roomId),
        });
      } else {
        setFormData({
          ...formData,
          managedRooms: [...formData.managedRooms, roomId],
        });
      }
    }
  };

  const handleSave = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    const saveData: Record<string, unknown> = {
      name: formData.name,
      email: formData.email,
      password: formData.password,
      role: formData.role,
      managedRooms: formData.role === "roommaster" ? formData.managedRooms : [],
    };

    try {
      if (editingUser) {
        const updateData = { ...saveData };
        const newPw = updateData.password as string;
        if (!newPw) {
          delete updateData.password;
        }
        if (editingUser._id === currentUserId) {
          delete updateData.role;
        }
        await usersAPI.update(editingUser._id, updateData);
        if (newPw) {
          setSavedPassword(newPw);
        } else {
          toast.success("Cập nhật người dùng thành công");
        }
      } else {
        if (!saveData.password) {
          toast.error("Vui lòng nhập mật khẩu");
          return;
        }
        if ((saveData.password as string).length < 8) {
          toast.error("Mật khẩu phải có ít nhất 8 ký tự");
          return;
        }
        await usersAPI.create(saveData);
        setSavedPassword(saveData.password as string);
      }
      setShowModal(false);
      fetchUsers();
    } catch (error: any) {
      console.error("Save user error:", error.response?.data || error.message);
      toast.error(error.response?.data?.message || "Có lỗi xảy ra");
    }
  };

  const formatManagedRooms = (user: User): string => {
    if (user.role === "user") return "—";
    if (user.role === "superadmin" || user.role === "admin") return "Tất cả phòng";
    if (Array.isArray(user.managedRooms) && user.managedRooms.length > 0) {
      const roomNames = user.managedRooms.map((roomId) => {
        const id = typeof roomId === "object" && roomId._id ? roomId._id : roomId as string;
        const room = rooms.find((r) => r._id === id);
        return room ? room.name : id;
      });
      return roomNames.join(", ");
    }
    return "Không quản lý phòng nào";
  };

  const getRoleBadge = (user: User): React.ReactElement => {
    const roleConfig: Record<string, { color: string; label: string }> = {
      superadmin: { color: "bg-yellow-100 text-yellow-800", label: "👑 Super Admin" },
      admin: { color: "bg-coffee-100 text-coffee-800", label: "🔧 Admin" },
      roommaster: { color: "bg-blue-100 text-blue-800", label: "🏠 Room Master" },
      kiemkho: { color: "bg-emerald-100 text-emerald-800", label: "📦 Kiểm Kho" },
      user: { color: "bg-gray-100 text-gray-800", label: "👤 User" },
    };
    const config = roleConfig[user.role] || roleConfig.user;
    return (
      <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const roleLabels: Record<UserRole, string> = {
    user: "User",
    roommaster: "Room Master",
    kiemkho: "Kiểm Kho",
    admin: "Admin",
    superadmin: "Super Admin",
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString("vi-VN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Đang tải...</div>
      </div>
    );
  }

  const isEditingMyself = !!(editingUser && editingUser._id === currentUserId);
  const roleOptions = editingUser ? getEditRoleOptions() : getRoleOptions();

  return (
    <div>

      {/* ── Saved Password Modal ── */}
      {savedPassword && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full text-center">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">Lưu mật khẩu này!</h3>
            <p className="text-sm text-gray-500 mb-5">Gửi cho người dùng trước khi đóng. Mật khẩu sẽ không hiển thị lại.</p>
            <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl px-5 py-4 mb-5 flex items-center justify-between gap-3">
              <span className="font-mono text-lg font-bold text-gray-900 tracking-widest select-all">{savedPassword}</span>
              <button
                onClick={() => { navigator.clipboard.writeText(savedPassword); toast.success("Đã copy!"); }}
                className="p-2 bg-coffee-100 hover:bg-coffee-200 text-coffee-700 rounded-lg transition-colors flex-shrink-0"
                title="Copy"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
              </button>
            </div>
            <button
              onClick={() => setSavedPassword(null)}
              className="w-full py-2.5 bg-coffee-600 hover:bg-coffee-700 text-white rounded-xl font-semibold transition-colors"
            >
              Đã lưu, đóng lại
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Quản lý người dùng
          </h1>
          <p className="text-gray-600 mt-1">Quản lý tài khoản và phân quyền</p>
        </div>
        {canAdd && (
          <button
            onClick={handleAddNew}
            className="px-4 py-2 bg-coffee-600 text-white rounded-lg hover:bg-coffee-700 flex items-center space-x-2"
          >
            <span>➕</span>
            <span>Thêm người dùng</span>
          </button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="text-4xl mr-4">👥</div>
            <div>
              <p className="text-sm text-gray-600">Tổng người dùng</p>
              <p className="text-2xl font-bold text-gray-900">{users.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="text-4xl mr-4">👑</div>
            <div>
              <p className="text-sm text-gray-600">Super Admin</p>
              <p className="text-2xl font-bold text-gray-900">
                {users.filter((u) => u.role === "superadmin").length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="text-4xl mr-4">🔧</div>
            <div>
              <p className="text-sm text-gray-600">Admin</p>
              <p className="text-2xl font-bold text-gray-900">
                {users.filter((u) => u.role === "admin").length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="text-4xl mr-4">🏠</div>
            <div>
              <p className="text-sm text-gray-600">Room Master</p>
              <p className="text-2xl font-bold text-gray-900">
                {users.filter((u) => u.role === "roommaster").length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="text-4xl mr-4">👤</div>
            <div>
              <p className="text-sm text-gray-600">User</p>
              <p className="text-2xl font-bold text-gray-900">
                {users.filter((u) => u.role === "user").length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="bg-white rounded-lg shadow-sm p-4 flex flex-wrap gap-3 items-center mb-6">
        <input
          type="text"
          placeholder="Tìm tên, email..."
          value={search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-coffee-300"
        />
        <select
          value={filterRole}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilterRole(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-coffee-300 bg-white"
        >
          <option value="all">Tất cả vai trò</option>
          <option value="superadmin">👑 Super Admin</option>
          <option value="admin">🔧 Admin</option>
          <option value="roommaster">🏠 Room Master</option>
          <option value="kiemkho">📦 Kiểm Kho</option>
          <option value="user">👤 User</option>
        </select>
        {(search || filterRole !== "all") && (
          <button
            onClick={() => { setSearch(""); setFilterRole("all"); }}
            className="text-xs text-coffee-600 hover:underline"
          >
            Xóa bộ lọc
          </button>
        )}
        <span className="text-sm text-gray-500 ml-auto">
          {filteredUsers.length} / {users.length} người dùng
        </span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tên</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vai trò</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phòng quản lý</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ngày tạo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hành động</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pageUsers.map((user) => (
                <tr key={user._id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setViewingUser(user)}>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-coffee-100 flex items-center justify-center mr-3">
                        <span className="text-coffee-600 font-bold text-lg">
                          {user.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {user.name}
                          {user._id === currentUserId && (
                            <span className="ml-2 text-xs text-coffee-500">(bạn)</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{user.email}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1">
                      {getRoleBadge(user)}
                      {(user as any).isBanned && (
                        <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700 w-fit">🔒 Bị khóa</span>
                      )}
                      {user.cmndStatus === "pending" && (
                        <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-700 w-fit">🪪 Chờ duyệt CCCD</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-600">
                      {formatManagedRooms(user)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {user.createdAt ? formatDate(user.createdAt) : "—"}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm" onClick={e => e.stopPropagation()}>
                    <div className="flex space-x-2">
                      {canEdit(user) && (
                        <button
                          onClick={() => handleEdit(user)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          ✏️ Sửa
                        </button>
                      )}
                      {isSuperAdmin && currentUserId !== user._id && user.role !== "superadmin" && (
                        (user as any).isBanned ? (
                          <button onClick={() => handleUnban(user)} className="text-green-600 hover:text-green-800">
                            🔓 Mở khóa
                          </button>
                        ) : (
                          <button onClick={() => { setBanTarget(user); setBanReason(""); }} className="text-orange-500 hover:text-orange-700">
                            🔒 Khóa
                          </button>
                        )
                      )}
                      {isSuperAdmin && currentUserId !== user._id && (user.role !== "superadmin" || isMasterAdmin) && (
                        <button
                          onClick={() => handleDelete(user._id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          🗑️ Xóa
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {users.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">👥</div>
            <p className="text-gray-500">Chưa có người dùng nào</p>
          </div>
        )}
      </div>

      <Pagination page={page} totalPages={totalPages} total={filteredUsers.length} pageSize={PAGE_SIZE} onChange={setPage} />

      {/* Modal: View User Detail */}
      {viewingUser && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setViewingUser(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-lg font-bold text-gray-900">Thông tin người dùng</h2>
              <button onClick={() => setViewingUser(null)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>

            <div className="p-5 space-y-5">
              {/* Avatar + basic info */}
              <div className="flex items-center gap-4">
                {viewingUser.avatarUrl ? (
                  <img src={viewingUser.avatarUrl} alt={viewingUser.name} className="w-16 h-16 rounded-full object-cover border-2 border-coffee-200" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-coffee-100 flex items-center justify-center text-2xl font-bold text-coffee-600">
                    {viewingUser.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-xl font-bold text-gray-900">{viewingUser.name}</h3>
                    {viewingUser._id === currentUserId && <span className="text-xs text-coffee-500">(bạn)</span>}
                  </div>
                  <p className="text-sm text-gray-500">{viewingUser.email}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {getRoleBadge(viewingUser)}
                    {viewingUser.verifiedLevel === 2 ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200 font-medium">✅ Xác thực cấp 2</span>
                    ) : viewingUser.cmndStatus === "pending" ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200 font-medium">🪪 Chờ duyệt CCCD</span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200 font-medium">✉️ Xác thực email</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Info rows */}
              <div className="bg-gray-50 rounded-xl divide-y divide-gray-100">
                <div className="flex justify-between px-4 py-2.5 text-sm">
                  <span className="text-gray-500">Ngày tạo</span>
                  <span className="font-medium">{viewingUser.createdAt ? formatDate(viewingUser.createdAt) : "—"}</span>
                </div>
                <div className="flex justify-between px-4 py-2.5 text-sm">
                  <span className="text-gray-500">Phòng quản lý</span>
                  <span className="font-medium text-right max-w-[60%]">{formatManagedRooms(viewingUser)}</span>
                </div>
                <div className="flex justify-between px-4 py-2.5 text-sm">
                  <span className="text-gray-500">Voucher đã lưu</span>
                  <span className="font-medium">
                    {viewingUser.claimedVouchers && viewingUser.claimedVouchers.length > 0
                      ? viewingUser.claimedVouchers.join(", ")
                      : "Chưa có"}
                  </span>
                </div>
              </div>

              {/* CMND / Căn cước */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Căn cước công dân (CCCD)</h4>
                {viewingUser.cmndImageUrl ? (
                  <div className="relative group">
                    <img
                      src={viewingUser.cmndImageUrl}
                      alt="Căn cước"
                      className="w-full rounded-xl border border-gray-200 object-cover max-h-60"
                    />
                    <a
                      href={viewingUser.cmndImageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl text-white text-sm font-medium"
                    >
                      🔍 Xem ảnh gốc
                    </a>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 bg-gray-50 border border-dashed border-gray-300 rounded-xl px-4 py-6 text-gray-400 text-sm justify-center">
                    <span>🪪</span> Chưa tải lên căn cước
                  </div>
                )}

                {viewingUser.cmndStatus === "pending" && (
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => { handleApproveCmnd(viewingUser); setViewingUser(null); }}
                      className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
                    >
                      ✅ Duyệt
                    </button>
                    <button
                      onClick={() => { handleRejectCmnd(viewingUser); setViewingUser(null); }}
                      className="flex-1 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg text-sm font-medium hover:bg-red-100"
                    >
                      ❌ Từ chối
                    </button>
                  </div>
                )}
                {viewingUser.cmndStatus === "rejected" && viewingUser.cmndRejectReason && (
                  <div className="mt-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                    Lý do từ chối lần trước: {viewingUser.cmndRejectReason}
                  </div>
                )}
              </div>
            </div>

            {/* Footer actions */}
            <div className="px-5 pb-5 flex justify-end gap-2 flex-wrap">
              {isSuperAdmin && currentUserId !== viewingUser._id && viewingUser.role !== "superadmin" && (
                (viewingUser as any).isBanned ? (
                  <button
                    onClick={() => { setViewingUser(null); handleUnban(viewingUser); }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
                  >
                    🔓 Mở khóa
                  </button>
                ) : (
                  <button
                    onClick={() => { setViewingUser(null); setBanTarget(viewingUser); setBanReason(""); }}
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm hover:bg-orange-600"
                  >
                    🔒 Khóa tài khoản
                  </button>
                )
              )}
              {isSuperAdmin && currentUserId !== viewingUser._id && (viewingUser.role !== "superadmin" || isMasterAdmin) && (
                <button
                  onClick={() => { setViewingUser(null); handleDelete(viewingUser._id); }}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700"
                >
                  🗑️ Xóa
                </button>
              )}
              {canEdit(viewingUser) && (
                <button
                  onClick={() => { setViewingUser(null); handleEdit(viewingUser); }}
                  className="px-4 py-2 bg-coffee-600 text-white rounded-lg text-sm hover:bg-coffee-700"
                >
                  ✏️ Chỉnh sửa
                </button>
              )}
              <button onClick={() => setViewingUser(null)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal xác nhận Ban */}
      {banTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-1">🔒 Khóa tài khoản</h2>
            <p className="text-sm text-gray-500 mb-4">
              Khóa <strong>{banTarget.name}</strong> — họ sẽ không thể đăng nhập nhưng vẫn xem được website.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Lý do khóa (tùy chọn)</label>
              <input
                type="text"
                value={banReason}
                onChange={e => setBanReason(e.target.value)}
                placeholder="Spam, vi phạm điều khoản..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => { setBanTarget(null); setBanReason(""); }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
              >
                Hủy
              </button>
              <button
                onClick={handleBan}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm hover:bg-orange-600 font-semibold"
              >
                Xác nhận khóa
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Add/Edit */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-2xl w-full my-8">
            <div className="p-6 max-h-[85vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingUser ? "Chỉnh sửa người dùng" : "Thêm người dùng mới"}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ✕
                </button>
              </div>

              {/* Permission note for admin */}
              {isAdmin && !isSuperAdmin && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-sm">
                  {editingUser && isEditingMyself
                    ? "Bạn chỉ có thể thay đổi tên và mật khẩu của chính mình."
                    : "Admin chỉ có thể thêm/sửa tài khoản User và Room Master."}
                </div>
              )}

              <form onSubmit={handleSave} className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tên *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-transparent"
                    placeholder="Nguyễn Văn A"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    required={!editingUser}
                    value={formData.email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-transparent ${editingUser ? "bg-gray-100 text-gray-500" : ""}`}
                    placeholder="user@example.com"
                    readOnly={!!editingUser}
                  />
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mật khẩu {editingUser ? "(để trống nếu không đổi)" : "*"}
                  </label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <input
                        type={showPassword ? "text" : "password"}
                        required={!editingUser}
                        value={formData.password}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setFormData({ ...formData, password: e.target.value })
                        }
                        className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-transparent"
                        placeholder={editingUser ? "Nhập để thay đổi mật khẩu" : "Tối thiểu 8 ký tự"}
                        minLength={formData.password ? 8 : undefined}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? (
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                        )}
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const pw = generatePassword();
                        setFormData({ ...formData, password: pw });
                        setShowPassword(true);
                      }}
                      className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs rounded-lg border border-gray-300 whitespace-nowrap transition-colors"
                      title="Tạo mật khẩu ngẫu nhiên"
                    >
                      🔀 Tạo tự động
                    </button>
                  </div>
                </div>

                {/* Role */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Vai trò *</label>
                  <select
                    value={formData.role}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                      setFormData({ ...formData, role: e.target.value as UserRole })
                    }
                    disabled={isEditingMyself}
                    className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-transparent ${isEditingMyself ? "bg-gray-100 text-gray-500" : ""}`}
                  >
                    {roleOptions.map((role) => (
                      <option key={role} value={role}>
                        {roleLabels[role]}
                      </option>
                    ))}
                  </select>
                  {isEditingMyself && (
                    <p className="text-xs text-gray-400 mt-1">Không thể thay đổi vai trò của chính mình.</p>
                  )}
                </div>

                {/* Managed Rooms */}
                {formData.role === "roommaster" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phòng được quản lý *
                    </label>
                    <div className="p-4 border rounded-lg bg-gray-50 space-y-2">
                      {rooms.length === 0 ? (
                        <p className="text-sm text-gray-500">Chưa có phòng nào. Vui lòng thêm phòng trước.</p>
                      ) : (
                        rooms.map((room) => (
                          <label
                            key={room._id}
                            className="flex items-center space-x-3 cursor-pointer hover:bg-white p-2 rounded"
                          >
                            <input
                              type="checkbox"
                              checked={Array.isArray(formData.managedRooms) && formData.managedRooms.includes(room._id)}
                              onChange={() => toggleRoomSelection(room._id)}
                              className="w-4 h-4 text-coffee-600 rounded"
                            />
                            <div className="flex items-center space-x-2">
                              <img
                                src={room.images?.[0] || "https://via.placeholder.com/40"}
                                alt={room.name}
                                className="w-10 h-10 object-cover rounded"
                                onError={(e: React.SyntheticEvent<HTMLImageElement>) =>
                                  (e.currentTarget.src = "https://via.placeholder.com/40")
                                }
                              />
                              <div>
                                <p className="text-sm font-medium text-gray-900">{room.name}</p>
                                <p className="text-xs text-gray-500">{room.district}</p>
                              </div>
                            </div>
                          </label>
                        ))
                      )}
                      {Array.isArray(formData.managedRooms) && formData.managedRooms.length > 0 && (
                        <p className="text-xs text-gray-600 mt-2 pt-2 border-t">
                          Đã chọn: {formData.managedRooms.length} phòng
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Buttons */}
                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 bg-coffee-600 text-white py-2 px-4 rounded-lg hover:bg-coffee-700 font-semibold"
                  >
                    {editingUser ? "Cập nhật" : "Thêm người dùng"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 font-semibold"
                  >
                    Hủy
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UsersPage;
